import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Printer, ArrowLeft, UserCircle, Pill, Stethoscope, CalendarBlank,
  IdentificationCard, Storefront, Phone, Warning, HeartStraight
} from "@phosphor-icons/react";

export default function CareSummary() {
  const { patientId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/patients/${patientId}/summary`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [patientId]);

  if (loading) return <div className="p-8 text-[#576A62]">Loading care summary…</div>;
  if (!data) return <div className="p-8 text-[#576A62]">Not found</div>;

  const p = data.patient;
  const generated = new Date(data.generated_at).toLocaleString();

  return (
    <div className="min-h-screen bg-[#FDFBF7] pb-24" data-testid="care-summary-page">
      {/* Header (screen only) */}
      <div className="print:hidden bg-white border-b border-[#EBE8E0]">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <Link to="/app" className="inline-flex items-center gap-2 text-[#576A62] hover:text-[#2C5545]">
            <ArrowLeft size={18} /> Back to app
          </Link>
          <div className="text-xs uppercase tracking-widest text-[#576A62]">Care summary</div>
          <button
            onClick={() => window.print()}
            data-testid="care-summary-print"
            className="rounded-full bg-[#2C5545] hover:bg-[#224235] text-white font-semibold px-5 py-2 inline-flex items-center gap-2"
          >
            <Printer size={18} weight="duotone" /> Print / Save PDF
          </button>
        </div>
      </div>

      {/* Sheet */}
      <div className="max-w-4xl mx-auto px-6 md:px-8 py-10 print:px-0 print:py-0">
        <div className="bg-white border border-[#EBE8E0] rounded-2xl p-8 md:p-10 print:border-0 print:rounded-none print:p-6 print:shadow-none">
          {/* Title block */}
          <div className="flex items-start justify-between gap-6 pb-6 border-b border-[#EBE8E0]">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#576A62] font-semibold">EverCara · care summary</div>
              <h1 className="mt-2 font-heading text-3xl sm:text-4xl font-bold" data-testid="summary-patient-name">
                {p.name}
              </h1>
              <div className="mt-2 text-[#576A62]">
                {p.relationship && <>Relationship: <span className="font-medium text-[#1A2520]">{p.relationship}</span></>}
                {p.date_of_birth && <> · DOB: <span className="font-medium text-[#1A2520]">{p.date_of_birth}</span></>}
                {p.gender && <> · <span className="font-medium text-[#1A2520]">{p.gender}</span></>}
                {p.blood_type && <> · Blood: <span className="font-medium text-[#1A2520]">{p.blood_type}</span></>}
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="w-16 h-16 rounded-full bg-[#EBE8E0] flex items-center justify-center ml-auto print:hidden">
                <UserCircle size={44} weight="duotone" className="text-[#2C5545]" />
              </div>
              <div className="text-xs text-[#576A62] mt-2">Generated {generated}</div>
              <div className="text-xs text-[#576A62]">by {data.generated_by.name || data.generated_by.email}</div>
            </div>
          </div>

          {/* Critical info */}
          {(p.allergies || p.conditions || p.emergency_contact_name) && (
            <div className="mt-6 grid md:grid-cols-3 gap-4">
              {p.allergies && (
                <Card testid="summary-allergies" tone="warning" icon={Warning} title="Allergies">
                  <div className="font-semibold">{p.allergies}</div>
                </Card>
              )}
              {p.conditions && (
                <Card testid="summary-conditions" icon={HeartStraight} title="Conditions">
                  <div className="font-semibold">{p.conditions}</div>
                </Card>
              )}
              {p.emergency_contact_name && (
                <Card testid="summary-emergency" icon={Phone} title="Emergency contact">
                  <div className="font-semibold">{p.emergency_contact_name}</div>
                  {p.emergency_contact_phone && <div className="text-sm">{p.emergency_contact_phone}</div>}
                </Card>
              )}
            </div>
          )}

          {/* Medications */}
          <Section title="Active medications" icon={Pill} count={data.active_medications.length} testid="summary-medications">
            {data.active_medications.length === 0 ? (
              <Empty text="None recorded" />
            ) : (
              <div className="grid sm:grid-cols-2 gap-3">
                {data.active_medications.map((m) => (
                  <div key={m.id} data-testid={`summary-med-${m.id}`} className="rounded-xl border border-[#EBE8E0] p-4 break-inside-avoid">
                    <div className="flex items-baseline justify-between gap-2">
                      <div className="font-heading text-lg font-bold">{m.name}</div>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${m.kind === "prescription" ? "bg-[#2C5545]/10 text-[#2C5545]" : "bg-[#C86A51]/12 text-[#B05A43]"}`}>
                        {m.kind === "prescription" ? "Rx" : "OTC"}
                      </span>
                    </div>
                    <div className="text-sm text-[#576A62]">{[m.strength, m.form].filter(Boolean).join(" · ")}</div>
                    {m.schedule && <div className="text-sm mt-1"><span className="text-[#576A62]">Schedule:</span> {m.schedule}</div>}
                    {m.purpose && <div className="text-sm"><span className="text-[#576A62]">For:</span> {m.purpose}</div>}
                    {m.instructions && <div className="text-sm text-[#576A62] mt-1">{m.instructions}</div>}
                    {m.prescriber && <div className="text-xs text-[#576A62] mt-2">Prescribed by {m.prescriber}</div>}
                  </div>
                ))}
              </div>
            )}
          </Section>

          {/* Doctors */}
          <Section title="Care team" icon={Stethoscope} count={data.doctors.length} testid="summary-doctors">
            {data.doctors.length === 0 ? <Empty text="None recorded" /> : (
              <div className="grid sm:grid-cols-2 gap-3">
                {data.doctors.map((d) => (
                  <div key={d.id} data-testid={`summary-doctor-${d.id}`} className="rounded-xl border border-[#EBE8E0] p-4 break-inside-avoid">
                    <div className="font-heading font-bold">{d.name}</div>
                    {d.specialty && <div className="text-sm text-[#576A62]">{d.specialty}</div>}
                    {d.practice && <div className="text-sm">{d.practice}</div>}
                    {d.phone && <div className="text-sm mt-1">☎ {d.phone}</div>}
                    {d.address && <div className="text-xs text-[#576A62] mt-1">{d.address}</div>}
                  </div>
                ))}
              </div>
            )}
          </Section>

          {/* Insurance */}
          {data.insurance.length > 0 && (
            <Section title="Insurance" icon={IdentificationCard} count={data.insurance.length} testid="summary-insurance">
              <div className="grid sm:grid-cols-2 gap-3">
                {data.insurance.map((c) => (
                  <div key={c.id} data-testid={`summary-insurance-${c.id}`} className="rounded-xl border border-[#EBE8E0] p-4 break-inside-avoid">
                    <div className="font-heading font-bold">{c.provider}</div>
                    {c.plan_name && <div className="text-sm text-[#576A62]">{c.plan_name}</div>}
                    <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
                      {c.policy_number && <><span className="text-[#576A62]">Policy</span><span>{c.policy_number}</span></>}
                      {c.group_number && <><span className="text-[#576A62]">Group</span><span>{c.group_number}</span></>}
                      {c.member_id && <><span className="text-[#576A62]">Member</span><span>{c.member_id}</span></>}
                      {c.phone && <><span className="text-[#576A62]">Support</span><span>{c.phone}</span></>}
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Pharmacies */}
          {data.pharmacies.length > 0 && (
            <Section title="Pharmacies" icon={Storefront} count={data.pharmacies.length} testid="summary-pharmacies">
              <div className="grid sm:grid-cols-2 gap-3">
                {data.pharmacies.map((ph) => (
                  <div key={ph.id} className="rounded-xl border border-[#EBE8E0] p-4 break-inside-avoid">
                    <div className="font-heading font-bold">{ph.name}</div>
                    {ph.phone && <div className="text-sm">☎ {ph.phone}</div>}
                    {ph.address && <div className="text-sm text-[#576A62]">{ph.address}</div>}
                    {ph.hours && <div className="text-xs text-[#576A62] mt-1">{ph.hours}</div>}
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Upcoming appointments */}
          {data.upcoming_appointments.length > 0 && (
            <Section title="Upcoming (next 30 days)" icon={CalendarBlank} count={data.upcoming_appointments.length} testid="summary-appointments">
              <ul className="space-y-2">
                {data.upcoming_appointments.map((a) => (
                  <li key={a.id} className="flex items-baseline gap-3 border-b border-[#EBE8E0]/60 pb-2 break-inside-avoid">
                    <span className="font-mono text-sm text-[#2C5545] w-40 shrink-0">
                      {a.date ? new Date(a.date).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                    </span>
                    <div className="flex-1">
                      <div className="font-medium">{a.title || a.doctor_name || "Appointment"}</div>
                      <div className="text-xs text-[#576A62]">{[a.doctor_name, a.location].filter(Boolean).join(" · ")}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          <div className="mt-10 pt-4 border-t border-[#EBE8E0] text-xs text-[#576A62] flex justify-between">
            <span>Generated by EverCara · {generated}</span>
            <span>Page 1</span>
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          @page { size: A4; margin: 12mm; }
          body { background: white !important; }
          .break-inside-avoid { break-inside: avoid; }
        }
      `}</style>
    </div>
  );
}

function Section({ title, icon: Icon, count, children, testid }) {
  return (
    <section className="mt-8 break-inside-avoid" data-testid={testid}>
      <div className="flex items-baseline gap-2 mb-3 pb-1 border-b-2 border-[#2C5545]/20">
        <Icon size={20} weight="duotone" className="text-[#2C5545] self-center" />
        <h2 className="font-heading text-xl font-bold">{title}</h2>
        {typeof count === "number" && (
          <span className="text-xs text-[#576A62]">· {count}</span>
        )}
      </div>
      {children}
    </section>
  );
}

function Card({ icon: Icon, title, children, tone, testid }) {
  const cls = tone === "warning"
    ? "border-[#D49A4C]/50 bg-[#D49A4C]/10"
    : "border-[#EBE8E0] bg-[#FDFBF7]";
  return (
    <div data-testid={testid} className={`rounded-xl border p-4 ${cls}`}>
      <div className="text-xs uppercase tracking-widest text-[#576A62] font-semibold flex items-center gap-1">
        <Icon size={14} weight="duotone" /> {title}
      </div>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function Empty({ text }) {
  return <div className="text-sm text-[#576A62] italic">{text}</div>;
}
